import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import React, { useState } from "react";

interface RefundDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: RefundData) => void;
  sessionData: any;
  loading?: boolean;
}

export interface RefundData {
  reason: string;
  amount?: number;
  refundType: "full" | "partial";
}

export const RefundDialog = ({
  open,
  onClose,
  onConfirm,
  sessionData,
  loading = false,
}: RefundDialogProps) => {
  const [refundType, setRefundType] = useState<"full" | "partial">("full");
  const [reason, setReason] = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const [errors, setErrors] = useState<{ reason?: string; amount?: string }>(
    {}
  );

  const totalAmount = sessionData?.sessionPayment?.totalAmount || 0;

  const handleSubmit = () => {
    const newErrors: { reason?: string; amount?: string } = {};

    if (!reason.trim()) {
      newErrors.reason = "Refund reason is required";
    }

    if (refundType === "partial") {
      const amount = parseFloat(customAmount);
      if (!customAmount || isNaN(amount)) {
        newErrors.amount = "Please enter a valid amount";
      } else if (amount <= 0) {
        newErrors.amount = "Amount must be greater than 0";
      } else if (amount > totalAmount) {
        newErrors.amount = `Amount cannot exceed ${totalAmount}`;
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const refundData: RefundData = {
      reason: reason.trim(),
      refundType,
    };

    if (refundType === "partial") {
      refundData.amount = parseFloat(customAmount);
    }

    onConfirm(refundData);
  };

  const handleClose = () => {
    if (!loading) {
      setRefundType("full");
      setReason("");
      setCustomAmount("");
      setErrors({});
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Process Refund</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            This action will process a refund for this session. Please ensure
            all details are correct before proceeding.
          </Alert>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Session ID: {sessionData?.sessionId}
            </Typography>
            <Typography variant="subtitle2" gutterBottom>
              Total Paid Amount: {totalAmount} SAR
            </Typography>
            <Typography variant="subtitle2" gutterBottom>
              Patient: {sessionData?.patientProfile?.fullName}
            </Typography>
          </Box>

          <FormControl component="fieldset" sx={{ mb: 3, width: "100%" }}>
            <FormLabel component="legend">Refund Type</FormLabel>
            <RadioGroup
              value={refundType}
              onChange={(e) =>
                setRefundType(e.target.value as "full" | "partial")
              }
            >
              <FormControlLabel
                value="full"
                control={<Radio />}
                label={`Full Refund (${totalAmount} SAR)`}
                disabled={loading}
              />
              <FormControlLabel
                value="partial"
                control={<Radio />}
                label="Partial Refund"
                disabled={loading}
              />
            </RadioGroup>
          </FormControl>

          {refundType === "partial" && (
            <TextField
              fullWidth
              label="Refund Amount"
              type="number"
              value={customAmount}
              onChange={(e) => {
                setCustomAmount(e.target.value);
                setErrors({ ...errors, amount: undefined });
              }}
              error={!!errors.amount}
              helperText={errors.amount || `Maximum: ${totalAmount} SAR`}
              sx={{ mb: 3 }}
              disabled={loading}
              inputProps={{
                step: "0.01",
                min: "0",
                max: totalAmount,
              }}
            />
          )}

          <TextField
            fullWidth
            label="Refund Reason"
            multiline
            rows={4}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setErrors({ ...errors, reason: undefined });
            }}
            error={!!errors.reason}
            helperText={
              errors.reason || "Please provide a reason for the refund"
            }
            placeholder="e.g., Session cancelled by patient, Technical issues, etc."
            disabled={loading}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="error"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? "Processing..." : "Process Refund"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
